<template>
  <v-card>
    <v-card-title class="text-h5">
      Detalle del Log
      <v-spacer></v-spacer>
      <v-chip
        :color="getStatusColor(log.status)"
        :text-color="log.status === 'pending' ? 'black' : 'white'"
      >
        <v-icon small left>{{ getStatusIcon(log.status) }}</v-icon>
        {{ getStatusLabel(log.status) }}
      </v-chip>
      <v-btn icon @click="$emit('close')" class="ml-2">
        <v-icon>mdi-close</v-icon>
      </v-btn>
    </v-card-title>

    <v-divider></v-divider>

    <v-card-text class="pt-4" style="max-height: 80vh; overflow-y: auto;">
      <!-- Información general -->
      <v-row>
        <v-col cols="12" md="6">
          <v-card outlined>
            <v-card-subtitle class="pb-0">Información General</v-card-subtitle>
            <v-card-text>
              <v-simple-table dense>
                <tbody>
                  <tr>
                    <td class="font-weight-bold">ID del Log:</td>
                    <td>{{ log._id }}</td>
                  </tr>
                  <tr>
                    <td class="font-weight-bold">Evento:</td>
                    <td>{{ log.event_name }}</td>
                  </tr>
                  <tr>
                    <td class="font-weight-bold">Tipo de entidad:</td>
                    <td>{{ log.entity_type }}</td>
                  </tr>
                  <tr>
                    <td class="font-weight-bold">ID de entidad:</td>
                    <td>{{ log.entity_id }}</td>
                  </tr>
                  <tr>
                    <td class="font-weight-bold">Fecha de ejecución:</td>
                    <td>{{ formatDate(log.triggered_at) }}</td>
                  </tr>
                  <tr v-if="log.execution_time_ms !== undefined">
                    <td class="font-weight-bold">Tiempo de ejecución:</td>
                    <td>
                      <v-chip
                        x-small
                        :color="getExecutionTimeColor(log.execution_time_ms)"
                      >
                        {{ log.execution_time_ms }}ms
                      </v-chip>
                    </td>
                  </tr>
                </tbody>
              </v-simple-table>
            </v-card-text>
          </v-card>
        </v-col>

        <v-col cols="12" md="6">
          <v-card outlined>
            <v-card-subtitle class="pb-0">Request</v-card-subtitle>
            <v-card-text>
              <v-simple-table dense>
                <tbody>
                  <tr v-if="log.request_url">
                    <td class="font-weight-bold">URL:</td>
                    <td class="text-truncate" style="max-width: 300px;">
                      {{ log.request_url }}
                    </td>
                  </tr>
                  <tr v-if="log.request_method">
                    <td class="font-weight-bold">Método:</td>
                    <td>
                      <v-chip x-small color="primary">{{
                        log.request_method
                      }}</v-chip>
                    </td>
                  </tr>
                  <tr v-if="log.response_status">
                    <td class="font-weight-bold">Status HTTP:</td>
                    <td>
                      <v-chip
                        x-small
                        :color="getHttpStatusColor(log.response_status)"
                        text-color="white"
                      >
                        {{ log.response_status }}
                      </v-chip>
                    </td>
                  </tr>
                </tbody>
              </v-simple-table>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <!-- Error (si existe) -->
      <v-row v-if="log.error_message">
        <v-col cols="12">
          <v-alert type="error" text>
            <div class="font-weight-bold mb-2">Error:</div>
            <div class="text-body-2">{{ log.error_message }}</div>
            <v-expansion-panels v-if="log.error_stack" flat class="mt-3">
              <v-expansion-panel>
                <v-expansion-panel-header class="pa-0">
                  <span class="text-caption">Ver stack trace</span>
                </v-expansion-panel-header>
                <v-expansion-panel-content>
                  <pre class="text-caption error-stack">{{
                    log.error_stack
                  }}</pre>
                </v-expansion-panel-content>
              </v-expansion-panel>
            </v-expansion-panels>
          </v-alert>
        </v-col>
      </v-row>

      <!-- Request Headers -->
      <v-row
        v-if="
          log.request_headers && Object.keys(log.request_headers).length > 0
        "
      >
        <v-col cols="12">
          <v-card outlined>
            <v-card-subtitle>
              Request Headers
              <v-btn
                icon
                x-small
                @click="copyToClipboard(log.request_headers)"
                class="ml-2"
              >
                <v-icon x-small>mdi-content-copy</v-icon>
              </v-btn>
            </v-card-subtitle>
            <v-card-text>
              <pre class="json-viewer">{{
                formatJSON(log.request_headers)
              }}</pre>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <!-- Request Payload -->
      <v-row v-if="log.request_payload">
        <v-col cols="12">
          <v-card outlined>
            <v-card-subtitle>
              Request Payload
              <v-btn
                icon
                x-small
                @click="copyToClipboard(log.request_payload)"
                class="ml-2"
              >
                <v-icon x-small>mdi-content-copy</v-icon>
              </v-btn>
            </v-card-subtitle>
            <v-card-text>
              <pre class="json-viewer">{{
                formatJSON(log.request_payload)
              }}</pre>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <!-- Response Headers -->
      <v-row
        v-if="
          log.response_headers && Object.keys(log.response_headers).length > 0
        "
      >
        <v-col cols="12">
          <v-card outlined>
            <v-card-subtitle>
              Response Headers
              <v-btn
                icon
                x-small
                @click="copyToClipboard(log.response_headers)"
                class="ml-2"
              >
                <v-icon x-small>mdi-content-copy</v-icon>
              </v-btn>
            </v-card-subtitle>
            <v-card-text>
              <pre class="json-viewer">{{
                formatJSON(log.response_headers)
              }}</pre>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <!-- Response Body -->
      <v-row v-if="log.response_body">
        <v-col cols="12">
          <v-card outlined>
            <v-card-subtitle>
              Response Body
              <v-btn
                icon
                x-small
                @click="copyToClipboard(log.response_body)"
                class="ml-2"
              >
                <v-icon x-small>mdi-content-copy</v-icon>
              </v-btn>
            </v-card-subtitle>
            <v-card-text>
              <pre class="json-viewer">{{ formatJSON(log.response_body) }}</pre>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </v-card-text>

    <v-divider></v-divider>

    <v-card-actions>
      <v-spacer></v-spacer>
      <v-btn text @click="$emit('close')">
        Cerrar
      </v-btn>
    </v-card-actions>

    <!-- Snackbar para copiar -->
    <v-snackbar v-model="copySnackbar" :timeout="2000" color="success">
      Copiado al portapapeles
    </v-snackbar>
  </v-card>
</template>

<script>
export default {
  name: "WebhookLogDetail",
  props: {
    log: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      copySnackbar: false
    };
  },
  methods: {
    getStatusColor(status) {
      const colors = {
        success: "success",
        failed: "error",
        pending: "warning"
      };
      return colors[status] || "grey";
    },
    getStatusIcon(status) {
      const icons = {
        success: "mdi-check-circle",
        failed: "mdi-alert-circle",
        pending: "mdi-clock-outline"
      };
      return icons[status] || "mdi-help-circle";
    },
    getStatusLabel(status) {
      const labels = {
        success: "Exitoso",
        failed: "Fallido",
        pending: "Pendiente"
      };
      return labels[status] || status;
    },
    getHttpStatusColor(status) {
      if (status >= 200 && status < 300) return "success";
      if (status >= 300 && status < 400) return "info";
      if (status >= 400 && status < 500) return "warning";
      if (status >= 500) return "error";
      return "grey";
    },
    getExecutionTimeColor(time) {
      if (time < 500) return "success";
      if (time < 2000) return "warning";
      return "error";
    },
    formatDate(date) {
      if (!date) return "-";
      const d = new Date(date);
      return d.toLocaleString("es-ES", {
        year: "numeric",
        month: "long",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit"
      });
    },
    formatJSON(obj) {
      try {
        return JSON.stringify(obj, null, 2);
      } catch (e) {
        return obj;
      }
    },
    copyToClipboard(data) {
      const text =
        typeof data === "string" ? data : JSON.stringify(data, null, 2);
      navigator.clipboard.writeText(text).then(() => {
        this.copySnackbar = true;
      });
    }
  }
};
</script>

<style scoped>
.json-viewer {
  background-color: #f5f5f5;
  padding: 12px;
  border-radius: 4px;
  overflow-x: auto;
  max-height: 400px;
  font-family: "Monaco", "Menlo", "Ubuntu Mono", "Consolas", monospace;
  font-size: 12px;
  line-height: 1.5;
}

.error-stack {
  background-color: #ffebee;
  padding: 8px;
  border-radius: 4px;
  overflow-x: auto;
  font-family: "Monaco", "Menlo", "Ubuntu Mono", "Consolas", monospace;
  font-size: 11px;
  line-height: 1.4;
}

.text-truncate {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
</style>
